import { AxiosError } from 'axios';
import { apiClient, publicApiClient } from './apiService';
import {
  refreshSessionWhileLocked,
  storeTokens,
  withRequestTimeout,
  withSessionLock
} from './authSession';
import { User } from '@/types/User.interface';
import {
  LOG_IN,
  LOG_OUT,
  SEND_OTP,
  VERIFY_OTP,
  CHANGE_PASSWORD,
  FORGOT_PASSWORD,
  SWITCH_BRANCH,
  USERS
} from './endpoints';

export interface LoginData {
  userLoginData: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface SwitchBranchResponse {
  accessToken: string;
  refreshToken: string;
}

export interface OtpResponse {
  status: string | number;
  message: string;
}

export interface ForgotPasswordResponse {
  status: string | number;
  message: string;
  isExists: boolean;
}

export interface VerifyOtpResponse {
  status: string | number;
  valid: boolean;
}

export const authService = {
  async login(data: LoginData): Promise<LoginResponse> {
    const response = await publicApiClient.post<LoginResponse>(LOG_IN, data);
    return response.data;
  },

  async logout(): Promise<void> {
    await apiClient.get(LOG_OUT);
  },

  async sendOtp(data: { phone: string }): Promise<OtpResponse> {
    const response = await publicApiClient.post<OtpResponse>(SEND_OTP, data);
    return response.data;
  },

  async verifyOtp(data: {
    phone: string;
    otp: string;
  }): Promise<VerifyOtpResponse> {
    const response = await publicApiClient.post<VerifyOtpResponse>(
      VERIFY_OTP,
      data
    );
    return response.data;
  },

  async changePassword(data: {
    phone: string;
    newPassword: string;
    otp: string;
  }): Promise<OtpResponse> {
    const response = await publicApiClient.post<OtpResponse>(
      CHANGE_PASSWORD,
      data
    );
    return response.data;
  },

  async forgotPassword(data: {
    phone: string;
  }): Promise<ForgotPasswordResponse> {
    const response = await publicApiClient.post<ForgotPasswordResponse>(
      FORGOT_PASSWORD,
      data
    );
    return response.data;
  },

  /**
   * Şube veya yönetici görünümüne geçer ve yeni anahtarları saklar. Oturum
   * kilidi içinde çalışır; böylece süren bir yenileme ile iç içe geçmez.
   */
  async switchBranch(branchId: number | null): Promise<SwitchBranchResponse> {
    return withSessionLock(async () => {
      const send = () =>
        withRequestTimeout((signal) =>
          apiClient.post<SwitchBranchResponse>(
            SWITCH_BRANCH,
            { branchId },
            { skipAuthRefresh: true, signal }
          )
        );
      let response: Awaited<ReturnType<typeof send>>;
      try {
        response = await send();
      } catch (error) {
        if ((error as AxiosError)?.response?.status !== 401) throw error;
        await refreshSessionWhileLocked();
        response = await send();
      }
      storeTokens(response.data);
      return response.data;
    });
  },

  async getProfile(): Promise<User> {
    const response = await apiClient.get<User>(`${USERS}/profile`);
    return response.data;
  }
};
